/**
 * Credits on the page (docs/SPEC.md §15.1; P28-T08): the confirm with the
 * estimate before an expensive action, and the block with **Top up** when the
 * balance is below an estimate. One look everywhere: the change box, the
 * brief's answer, Start, Try again, the ways out, the true render and the
 * design studio's instruction box.
 */
import type { Shortfall } from "../api/client";
import {
  CREDITS,
  type ConfirmWhat,
  blockedSentence,
  confirmSentence,
} from "../copy/credits";
import { Button } from "./Button";

/** "This needs about {needed} credits; you have {available}." with **Top
 *  up** (Accounts' top-up page, a new tab). */
export function TopUp({
  shortfall,
  sentence,
}: {
  shortfall: Shortfall;
  /** A sentence of its own instead of the shortfall's. */
  sentence?: string;
}) {
  const known =
    shortfall.needed !== null &&
    shortfall.available !== null &&
    shortfall.needed > shortfall.available;
  return (
    <div
      role="alert"
      data-testid="top-up"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
    >
      <p className="m-0 min-w-0 flex-1 basis-48 text-sm text-slate-800">
        {sentence ??
          (known
            ? blockedSentence(shortfall.needed ?? 0, shortfall.available ?? 0)
            : CREDITS.blockedUnknown)}
      </p>{" "}
      <Button
        as="a"
        href={shortfall.top_up_url}
        target="_blank"
        rel="noreferrer"
        size="sm"
        variant="primary"
      >
        {CREDITS.topUp}
      </Button>
    </div>
  );
}

/** "{What} — about {credits} credits. Go ahead?" with **Go ahead** and **Not
 *  now**: the person's yes before an expensive action. */
export function PriceConfirm({
  what,
  credits,
  busy = false,
  onYes,
  onNo,
}: {
  what: ConfirmWhat;
  credits: number;
  busy?: boolean;
  onYes(): void;
  onNo(): void;
}) {
  return (
    <div
      role="group"
      aria-label={CREDITS.what[what]}
      data-testid="price-confirm"
      className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
    >
      <p className="m-0 text-sm font-semibold text-slate-900">
        {confirmSentence(what, credits)}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="primary"
          disabled={busy}
          aria-busy={busy}
          onClick={onYes}
        >
          {CREDITS.goAhead}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={onNo}
        >
          {CREDITS.notNow}
        </Button>
      </div>
    </div>
  );
}
